import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, getAppToken, setAppToken } from "../lib/api";
import { engine } from "../engine/MTProtoEngine";
import { useEngineStore } from "../store/useEngineStore";
import { sessionStore } from "../tg/sessionStore";
import { Banner, Button, Card, Input } from "../ui";

export function SetupLogin() {
  const qc = useQueryClient();
  const phase = useEngineStore((s) => s.phase);
  const me = useEngineStore((s) => s.me);
  const error = useEngineStore((s) => s.error);

  const [token, setToken] = useState(getAppToken());
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [credsSaved, setCredsSaved] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [proxy, setProxy] = useState(false);

  useEffect(() => {
    void (async () => {
      const id = await sessionStore.getApiId();
      if (id) {
        setApiId(String(id));
        setCredsSaved(true);
      }
      setProxy(await sessionStore.getProxyEnabled());
    })();
  }, []);

  const connected = phase === "connected";

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <h1 className="text-lg font-semibold">Configuración</h1>

      {/* APP TOKEN */}
      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-slate-300">1 · Token de acceso (APP_TOKEN)</h2>
        <p className="text-xs text-slate-500">
          Protege la API del Worker. Se guarda en este navegador.
        </p>
        <div className="flex gap-2">
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="APP_TOKEN" />
          <Button
            onClick={() => {
              setAppToken(token.trim());
              qc.invalidateQueries();
            }}
          >
            Guardar
          </Button>
        </div>
      </Card>

      {!connected && (
        <>
          {/* API CREDENTIALS */}
          <Card className="space-y-3">
            <h2 className="text-sm font-medium text-slate-300">2 · Credenciales de Telegram</h2>
            <p className="text-xs text-slate-500">
              Obtén <code>api_id</code> y <code>api_hash</code> en{" "}
              <a className="text-sky-400 underline" href="https://my.telegram.org" target="_blank" rel="noreferrer">
                my.telegram.org
              </a>
              . Se guardan SOLO en este navegador.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input value={apiId} onChange={(e) => setApiId(e.target.value)} placeholder="api_id (número)" />
              <Input type="password" value={apiHash} onChange={(e) => setApiHash(e.target.value)} placeholder="api_hash" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={proxy}
                onChange={async (e) => {
                  setProxy(e.target.checked);
                  await sessionStore.setProxyEnabled(e.target.checked);
                }}
              />
              Usar proxy WebSocket del Worker (solo si tu región bloquea Telegram)
            </label>
            <Button
              variant="secondary"
              onClick={async () => {
                const id = parseInt(apiId, 10);
                if (!id || !apiHash.trim()) return;
                await engine.setCredentials(id, apiHash.trim());
                setCredsSaved(true);
              }}
            >
              Guardar credenciales
            </Button>
            {credsSaved && <Banner kind="success">Credenciales guardadas.</Banner>}
          </Card>

          {/* PHONE */}
          <Card className="space-y-3">
            <h2 className="text-sm font-medium text-slate-300">3 · Iniciar sesión</h2>
            <div className="flex gap-2">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+34600000000" />
              <Button
                disabled={!credsSaved || !phone.trim() || phase === "connecting"}
                onClick={() => engine.startLogin(phone.trim())}
              >
                {phase === "connecting" ? "Conectando…" : "Enviar código"}
              </Button>
            </div>

            {phase === "awaitCode" && (
              <div className="flex gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Código recibido" />
                <Button onClick={() => engine.submitCode(code.trim())}>Confirmar código</Button>
              </div>
            )}
            {phase === "awaitPassword" && (
              <div className="flex gap-2">
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña 2FA" />
                <Button onClick={() => engine.submitPassword(password)}>Confirmar 2FA</Button>
              </div>
            )}
            {error && <Banner kind="danger">{error}</Banner>}
          </Card>
        </>
      )}

      {connected && (
        <Card className="space-y-3">
          <h2 className="text-sm font-medium text-slate-300">Sesión activa</h2>
          <p className="text-sm text-slate-300">
            Conectado como <strong>@{me?.username ?? me?.firstName ?? me?.id}</strong>
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => engine.init()}>
              Probar conexión
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await engine.logout();
                qc.clear();
              }}
            >
              Cerrar sesión
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
